/**
 * 重试拦截器
 */

import type { HttpRequest } from '../../../types/api'
import { CsdError } from '../../../types/error'
import { ERROR_CODES } from '../../../constants/errors'

export interface RetryConfig {
  maxRetries: number
  retryDelay: number
  retryableErrors: number[]
}

/** 带有请求配置的响应结构 */
interface ResponseWithConfig {
  config?: HttpRequest
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  retryableErrors: [ERROR_CODES.NETWORK_ERROR, ERROR_CODES.TIMEOUT_ERROR, ERROR_CODES.SERVER_ERROR]
}

/**
 * 延迟函数
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 创建重试拦截器
 */
export function createRetryInterceptor(httpClient: any, config: Partial<RetryConfig> = {}) {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config }

  return async (error: any): Promise<any> => {
    // 只处理 CsdError
    if (!(error instanceof CsdError)) {
      return Promise.reject(error)
    }

    // 检查是否是可重试的错误
    if (!retryConfig.retryableErrors.includes(error.code)) {
      return Promise.reject(error)
    }

    // 获取请求配置
    const response = error.response as ResponseWithConfig | undefined
    const request = response?.config
    if (!request) {
      return Promise.reject(error)
    }

    // 初始化重试计数
    if (!request._retryCount) {
      request._retryCount = 0
    }

    // 检查是否超过最大重试次数
    if (request._retryCount >= retryConfig.maxRetries) {
      return Promise.reject(error)
    }

    // 增加重试计数
    request._retryCount++

    // 延迟后重试
    await delay(retryConfig.retryDelay * request._retryCount)

    // 重新发送请求
    try {
      return await httpClient.request(request)
    } catch (retryError) {
      // 如果重试失败，继续递归处理
      return createRetryInterceptor(httpClient, config)(retryError)
    }
  }
}
